/* CatalogNeedsAttention — compact, opinionated "fix-me" surface.
 *
 * Replaces the abstract % score with a quiet badge + actionable popover.
 * Shows nothing when the catalog has zero issues (no noise). One click
 * a gap → BulkFixModal opens scoped to that gap → fix in one screen.
 *
 * Modes:
 *   compact (default)  — single inline badge "⚠ 12 products need attention"
 *                        with a hover popover listing each gap as a Fix link
 *   panel               — block panel for inline use right after import
 *                        ("Done — 248 imported. 12 need images. Fix now?")
 *
 * Data source — same /catalog-import/completeness endpoint as before,
 * but we ONLY use the `gaps[]` and `system_warnings[]` arrays. The score
 * field is ignored.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import {
  AlertTriangle, AlertCircle, ChevronRight, X,
} from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../ui/popover";
import BulkFixModal from "./BulkFixModal";


export default function CatalogNeedsAttention({ mode = "compact", onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixModal, setFixModal] = useState(null);  // {field, label} when open
  const [dismissedPanel, setDismissedPanel] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/seller/catalog-import/completeness");
      setData(r.data || null);
    } catch (_e) { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return null;

  // Filter — only show CRITICAL gaps. Stock + MOQ defaults are noise.
  const criticalGaps = (data.gaps || []).filter(g =>
    !["stock", "moq"].includes(g.field)
  );
  const totalIssues = criticalGaps.length + (data.system_warnings || []).length;
  if (totalIssues === 0) return null;
  if (data.total_products === 0) return null;

  const handleFixSaved = (n) => {
    load();
    onSaved && onSaved(n);
  };

  // ─── COMPACT badge mode (default) — for /seller/catalog header ─────
  if (mode === "compact") {
    const totalCount = criticalGaps.reduce((s, g) => s + g.count, 0);
    return (
      <>
        <Popover>
          <PopoverTrigger asChild>
            <button
              data-testid="needs-attention-badge"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-900 text-xs font-medium transition-colors">
              <AlertTriangle className="w-3.5 h-3.5"/>
              {totalCount > 0
                ? <>{totalCount} product{totalCount !== 1 ? "s" : ""} need attention</>
                : <>Catalog issue{totalIssues !== 1 ? "s" : ""}</>}
              <ChevronRight className="w-3 h-3 opacity-70"/>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <GapList
              gaps={criticalGaps}
              warnings={data.system_warnings || []}
              onFix={(g) => setFixModal(g)}
            />
          </PopoverContent>
        </Popover>
        {fixModal && (
          <BulkFixModal gap={fixModal} onClose={() => setFixModal(null)} onSaved={handleFixSaved}/>
        )}
      </>
    );
  }

  // ─── PANEL mode — for inline use (e.g. right after import) ─────────
  if (dismissedPanel) return null;
  return (
    <>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="needs-attention-panel">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center shrink-0">
            <AlertTriangle className="w-4 h-4"/>
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-amber-900">
              {data.total_products} product{data.total_products !== 1 ? "s" : ""} imported.
              {" "}{criticalGaps.length > 0 && <>Some need attention before going live.</>}
            </div>
            <div className="space-y-1 mt-2.5">
              {criticalGaps.map(g => (
                <button key={g.field} onClick={() => setFixModal(g)}
                  data-testid={`panel-fix-${g.field}`}
                  className="w-full text-left rounded-lg bg-white border border-amber-200 hover:border-amber-400 px-3 py-2 flex items-center gap-2 text-[12px] group transition-colors">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0"/>
                  <span className="flex-1 min-w-0 truncate">
                    <span className="mono text-foreground/65 mr-1.5">{g.count}</span>{g.label}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-900 group-hover:text-amber-800">
                    Fix in bulk <ChevronRight className="w-3 h-3"/>
                  </span>
                </button>
              ))}
              {(data.system_warnings || []).map(w => (
                <div key={w.key} className="rounded-lg bg-white border border-amber-200 px-3 py-2 text-[12px] text-amber-900 inline-flex items-center gap-2 w-full">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0"/>{w.message}
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => setDismissedPanel(true)} className="text-amber-900/55 hover:text-amber-900 shrink-0" data-testid="dismiss-attention-panel">
            <X className="w-4 h-4"/>
          </button>
        </div>
      </div>
      {fixModal && (
        <BulkFixModal gap={fixModal} onClose={() => setFixModal(null)} onSaved={handleFixSaved}/>
      )}
    </>
  );
}


function GapList({ gaps, warnings, onFix }) {
  return (
    <div className="p-3" data-testid="gap-list-popover">
      <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 mb-2 px-1">
        Needs attention
      </div>
      <div className="space-y-1">
        {gaps.map(g => (
          <button key={g.field} onClick={() => onFix(g)}
            data-testid={`pop-fix-${g.field}`}
            className="w-full text-left rounded-lg bg-secondary/40 hover:bg-secondary border border-border px-2.5 py-2 flex items-center gap-2 text-[12px] group transition-colors">
            <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0"/>
            <span className="flex-1 min-w-0 truncate">
              <span className="mono text-foreground/65 mr-1.5">{g.count}</span>{g.label}
            </span>
            <ChevronRight className="w-3 h-3 text-foreground/55 group-hover:text-foreground"/>
          </button>
        ))}
        {warnings.map(w => (
          <div key={w.key} className="rounded-lg bg-amber-50 border border-amber-200 text-[11.5px] text-amber-900 px-2.5 py-2 inline-flex items-center gap-2 w-full">
            <AlertCircle className="w-3.5 h-3.5 shrink-0"/>{w.message}
          </div>
        ))}
      </div>
      <div className="text-[10px] text-foreground/45 mt-2 px-1">
        Click any item to bulk-fix.
      </div>
    </div>
  );
}
